import { zodResolver } from '@hookform/resolvers/zod';
import { type MachineBillResource, MachineBillsController_reverse_v1 } from '@repo/api-client';
import { Alert, AlertDescription } from '@repo/ui/components/alert';
import { Button } from '@repo/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@repo/ui/components/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@repo/ui/components/form';
import { Input } from '@repo/ui/components/input';
import { IconAlertTriangle } from '@tabler/icons-react';
import { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { useApiClient } from '@/api/api-client';
import { messageForFailure } from '@/api/failure';
import { machineBillReversalReasonSchema } from '@/lib/field-schemas';
import { IDEMPOTENCY_KEY_HEADER } from '@/lib/idempotency-key';
import { formatPaise } from '@/lib/money';
import { attemptFor, type WriteAttempt } from '@/lib/write-attempt';

import { fieldLabel, fieldSurface } from '../auth/AuthFrame';
import { formatBusinessDate } from '../tickets/business-date';

/**
 * Reversing an imported bill: `POST /v1/machine-bills/{saleId}/reversal`.
 *
 * For a bill that should never have been imported — a test print, a bill keyed
 * on the wrong machine. Like a refund it is a compensating entry, never an edit:
 * the bill stays readable, marked.
 *
 * **It lands on the bill's own business day, not today's**, so that day must be
 * open; `409 BUSINESS_DAY_CLOSED` means an owner reopens it first. The dialog
 * says so before anyone presses, and the refusal copy says so again from its
 * code in `error-messages.ts`.
 *
 * **The key belongs to the reason, not to the mount.** The reason is editable
 * between attempts, and a changed body under a held key is
 * `409 SYNC_IDEMPOTENCY_KEY_REUSED`, non-retryable — see `write-attempt.ts`.
 */

const reverseSchema = z.object({ reason: machineBillReversalReasonSchema });

type ReverseValues = z.infer<typeof reverseSchema>;

export function ReverseMachineBillDialog({
  bill,
  onClose,
  onReversed,
}: {
  /** The bill being reversed, or `null` when none is. */
  bill: MachineBillResource | null;
  onClose: () => void;
  /** Handed the bill as it now stands — carrying its reversal. */
  onReversed: (bill: MachineBillResource) => void;
}) {
  return (
    <Dialog
      open={bill !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent>
        {/* A child, so closing unmounts it and the next reversal starts clean. */}
        {bill !== null && <ReverseForm bill={bill} onClose={onClose} onReversed={onReversed} />}
      </DialogContent>
    </Dialog>
  );
}

function ReverseForm({
  bill,
  onClose,
  onReversed,
}: {
  bill: MachineBillResource;
  onClose: () => void;
  onReversed: (bill: MachineBillResource) => void;
}) {
  const client = useApiClient();

  const form = useForm({
    resolver: zodResolver(reverseSchema),
    mode: 'onTouched',
    defaultValues: { reason: '' },
  });

  const [formError, setFormError] = useState<string | null>(null);

  const attempt = useRef<WriteAttempt | null>(null);

  const pending = form.formState.isSubmitting;

  const handleSubmit = async ({ reason }: ReverseValues) => {
    if (pending) return;

    setFormError(null);

    // The same reason re-sent is the same attempt and replays; a reworded one is
    // a new attempt with a new key.
    const sending = attemptFor(attempt.current, reason);
    attempt.current = sending;

    try {
      onReversed(
        await client.request(MachineBillsController_reverse_v1, {
          path: { saleId: bill.saleId },
          body: { reason },
          headers: { [IDEMPOTENCY_KEY_HEADER]: sending.key },
        }),
      );
    } catch (error: unknown) {
      // Worded from the code: BUSINESS_DAY_CLOSED, MACHINE_BILL_ALREADY_REVERSED,
      // MACHINE_BILL_REVERSAL_REASON_INVALID, AUTH_FORBIDDEN and the rest.
      setFormError(messageForFailure(error));
    }
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>
          Reverse bill <span className="font-mono numeric">{bill.receiptNumber}</span>?
        </DialogTitle>
        <DialogDescription>
          <span className="font-mono text-foreground numeric">{formatPaise(bill.totalPaise)}</span>{' '}
          comes off {bill.machineName}&rsquo;s takings. This cannot be undone.
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-3 rounded-2xl bg-muted px-4 py-3 text-sm text-foreground">
        <p>
          The reversal is recorded on the bill&rsquo;s own trading day,{' '}
          <span className="numeric">{formatBusinessDate(bill.businessDate)}</span>, not today. If
          that day has been closed, an owner has to reopen it first.
        </p>
        <p>
          The bill stays in the record, marked. Uploading the same file again imports it as a new
          revision.
        </p>
      </div>

      <Form {...form}>
        <form
          className="space-y-5"
          noValidate
          onSubmit={(event) => void form.handleSubmit(handleSubmit)(event)}
        >
          <FormField
            control={form.control}
            name="reason"
            render={({ field }) => (
              <FormItem>
                <FormLabel required className={fieldLabel}>
                  Reason
                </FormLabel>
                <FormControl
                  render={
                    <Input
                      className={`${fieldSurface} h-11`}
                      required
                      placeholder="Test print, not a sale"
                      autoComplete="off"
                      autoFocus
                      {...field}
                    />
                  }
                />
                <FormDescription>Why this bill should not have been imported.</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          {formError !== null && (
            <Alert variant="destructive">
              <IconAlertTriangle />
              <AlertDescription>{formError}</AlertDescription>
            </Alert>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={pending}>
              Cancel
            </Button>
            <Button type="submit" variant="destructive" disabled={pending}>
              {pending ? 'Reversing…' : `Reverse ${formatPaise(bill.totalPaise)}`}
            </Button>
          </DialogFooter>
        </form>
      </Form>
    </>
  );
}
